/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useMemo } from "react";
import {
  GridCellParams,
  GridColDef,
  GridEventListener,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import {
  HeatSeries,
  ChemicalElementSymbol,
  NonElementQuantity,
} from "src/store/api/generatedApi";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { useHeatsFilter } from "components/strategyDashboard/heats/context/HeatsFilterContext";
import { useChemistryElement } from "../heats/chemistryTab/ChemistryElementContext";
import { SteelGradeCell } from "../heats/cells/SteelGradeCell";
import { ChemistryViolationsCells } from "../heats/cells/ChemistryViolationsCells";
import { RequireNonAggregationRow } from "../heats/RequireNonAggregationRow";
import { ColouredCell } from "../ColouredCell";
import { useNumberSerialiser } from "hooks/serialisers/numbers";

const COLUMN_WIDTHS = {
  date: 100,
  heatId: 80,
  crewId: 60,
  steelGrade: 100,
  startTime: 88,
  tapTime: 88,
  materialPercent: 90,
  chemistryViolations: 250,
} as const;

interface OutlierHeatsTableProps {
  materialName: string;
}

const getMaterialPercentage = (
  heat: HeatSeries,
  materialName: string,
  source: string
): number | null => {
  const mix = heat.mixes.find((m) => m.source === source);
  if (!mix) return null;

  const totalMass = mix.mix.totals.reduce((sum, t) => sum + t.mass, 0);
  if (totalMass === 0) return null;

  const materialTotal = mix.mix.totals.find(
    (t) => t.material_name === materialName
  );
  if (!materialTotal) return 0;

  return (materialTotal.mass / totalMass) * 100;
};

export const OutlierHeatsTable = ({ materialName }: OutlierHeatsTableProps) => {
  const { t } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const gridApiRef = useGridApiRef();
  const theme = useTheme();
  const { data, isFetching } = useHeatsData();
  const { setSelectedHeatId, setSelectedTabKey } = useHeatsSelection();
  const { finalSampleOnly } = useHeatsFilter();
  const { toLocalDatetime } = useLocalDatetime();
  const { setSelectedElement } = useChemistryElement();

  const formatTime = useCallback(
    (date: string) => toLocalDatetime(date, "LT"),
    [toLocalDatetime]
  );

  const formatDate = useCallback(
    (date: string) => toLocalDatetime(date, "L"),
    [toLocalDatetime]
  );

  const formatPercent = useCallback(
    (value: number | null) => {
      if (value === null) return "-";
      return `${format(value, { decimalPlaces: 1 })}%`;
    },
    [format]
  );

  const handleElementClick = useCallback(
    (element: ChemicalElementSymbol | NonElementQuantity) => {
      setSelectedElement(element);
      setSelectedTabKey("chemistry");
    },
    [setSelectedElement, setSelectedTabKey]
  );

  // Calculate max absolute difference for coloring
  const maxAbsDiff = useMemo(() => {
    if (!data?.series) return 0;
    let max = 0;
    for (const heat of data.series) {
      const deployed = getMaterialPercentage(
        heat,
        materialName,
        "plannedSource"
      );
      const level2 = getMaterialPercentage(heat, materialName, "level2");
      if (deployed !== null && level2 !== null) {
        max = Math.max(max, Math.abs(deployed - level2));
      }
    }
    return max;
  }, [data?.series, materialName]);

  const availableConstraints = useMemo(
    () => data?.available_sample_constraints ?? [],
    [data?.available_sample_constraints]
  );

  const columns: GridColDef<HeatSeries>[] = useMemo(
    () => [
      {
        field: "heat_date",
        width: COLUMN_WIDTHS.date,
        headerName: t("date"),
        sortable: true,
        resizable: false,
        valueGetter: (_: unknown, row: HeatSeries) => row.start_timestamp,
        valueFormatter: (value: string | null) =>
          value ? formatDate(value) : null,
      },
      {
        field: "heat_id",
        width: COLUMN_WIDTHS.heatId,
        headerName: "#",
        sortable: false,
        headerAlign: "center",
        align: "center",
        resizable: false,
      },
      {
        field: "crew_id",
        width: COLUMN_WIDTHS.crewId,
        headerName: t("crew"),
        sortable: false,
        headerAlign: "center",
        align: "center",
        resizable: true,
      },
      {
        field: "steel_grade",
        minWidth: COLUMN_WIDTHS.steelGrade,
        headerName: t("steelGrade"),
        renderCell: RequireNonAggregationRow(({ row }) => (
          <SteelGradeCell
            steelGrade={row.steel_grade}
            originalSteelGrade={row.original_steel_grade}
          />
        )),
        sortable: false,
        resizable: false,
      },
      {
        field: "start_timestamp",
        width: COLUMN_WIDTHS.startTime,
        headerName: t("startNoun"),
        sortable: true,
        resizable: false,
        valueFormatter: (value) => (value ? formatTime(value) : null),
      },
      {
        field: "tap_timestamp",
        width: COLUMN_WIDTHS.tapTime,
        headerName: t("tap"),
        sortable: true,
        resizable: false,
        valueFormatter: (value) => (value ? formatTime(value) : null),
      },
      {
        field: "deployed_percent",
        width: COLUMN_WIDTHS.materialPercent,
        headerName: t("plannedSource"),
        headerAlign: "center",
        align: "center",
        sortable: true,
        resizable: false,
        valueGetter: (_: unknown, row: HeatSeries) =>
          getMaterialPercentage(row, materialName, "plannedSource"),
        valueFormatter: (value: number | null) => formatPercent(value),
      },
      {
        field: "level2_percent",
        width: COLUMN_WIDTHS.materialPercent,
        headerName: t("level2"),
        headerAlign: "center",
        align: "center",
        sortable: true,
        resizable: false,
        valueGetter: (_: unknown, row: HeatSeries) =>
          getMaterialPercentage(row, materialName, "level2"),
        valueFormatter: (value: number | null) => formatPercent(value),
      },
      {
        field: "difference",
        width: COLUMN_WIDTHS.materialPercent,
        headerName: t("difference"),
        headerAlign: "center",
        align: "center",
        sortable: true,
        resizable: false,
        cellClassName: "coloured-cell",
        valueGetter: (_: unknown, row: HeatSeries) => {
          const deployed = getMaterialPercentage(
            row,
            materialName,
            "plannedSource"
          );
          const level2 = getMaterialPercentage(row, materialName, "level2");
          if (deployed === null || level2 === null) return null;
          return level2 - deployed;
        },
        renderCell: (params: GridCellParams<HeatSeries>) => {
          const value = params.value as number | null;
          if (value === null) return "-";

          const absValue = Math.abs(value);
          const normalized = maxAbsDiff > 0 ? absValue / maxAbsDiff : 0;
          const alpha = Math.pow(normalized, 0.7) * 0.8;

          return (
            <ColouredCell
              content={formatPercent(value)}
              alpha={alpha}
              color={theme.palette.data.blue}
              justifyContent="center"
            />
          );
        },
      },
      {
        field: "chemistryViolations",
        width: COLUMN_WIDTHS.chemistryViolations,
        headerName: t("chemistryViolations"),
        sortable: false,
        resizable: false,
        aggregable: false,
        renderCell: RequireNonAggregationRow(({ row }) => (
          <ChemistryViolationsCells
            row={row}
            t={t}
            availableConstraints={availableConstraints}
            handleElementClick={handleElementClick}
            finalSampleOnly={finalSampleOnly}
            wrap
          />
        )),
      },
    ],
    [
      t,
      formatDate,
      formatTime,
      formatPercent,
      materialName,
      maxAbsDiff,
      theme.palette.data.blue,
      availableConstraints,
      handleElementClick,
      finalSampleOnly,
    ]
  );

  const handleOnClick: GridEventListener<"cellClick"> = useCallback(
    (params: GridCellParams<HeatSeries>) => {
      setSelectedHeatId(params.row.heat_id);
    },
    [setSelectedHeatId]
  );

  return (
    <HotkeyCompliantDataGrid
      apiRef={gridApiRef}
      rows={data?.series ?? []}
      showCellVerticalBorder={false}
      showColumnVerticalBorder
      columns={columns}
      onCellClick={handleOnClick}
      loading={isFetching}
      getRowId={(row) => row.heat_id}
      hideFooter
      getRowHeight={() => "auto"}
      sx={{
        minHeight: 300,
        "& .MuiDataGrid-columnHeaderTitle": {
          whiteSpace: "normal",
          lineHeight: "1.2rem",
        },
        "& .MuiDataGrid-row": {
          minHeight: "30px !important",
        },
        "& .MuiDataGrid-columnHeader": {
          height: "auto !important",
          maxHeight: "none !important",
          alignItems: "flex-start",
        },
        "& .MuiDataGrid-columnHeaders": {
          maxHeight: "none !important",
        },
        "& .MuiDataGrid-cell.coloured-cell": {
          padding: 0,
        },
      }}
    />
  );
};
